/*
This file is located in the main branch
Last time the file was updated (UTC): [02.07.2024 18:53:06]
Permissions to update outside the main branch: [false]
*/

import {
    checkPhoneLengthFromMask,
    DropDown,
    getMaskFromName,
    getMaskFromPhone,
    requiredRule,
    Tab,
    userNameRule,
    ValidatedDropDownList,
    ValidatedInput,
} from '@finbeat/react-toolkit';
import { t } from 'i18next';
import { UserItem } from 'models/api/users';
import { Key } from 'react';
import { useFormContext } from 'react-hook-form';
import { useUserWorkplaceState } from '../Users';

type MainTabProps = {
    disabled: boolean;
    toggle: (tab?: Key) => void;
    crmRoles: DropDown[];
    loadingCrmRoles: boolean;
};

const MainTab = ({ disabled, toggle, crmRoles, loadingCrmRoles }: MainTabProps) => {
    const { currentItem } = useUserWorkplaceState();
    const { watch } = useFormContext<UserItem>();

    const isExternalUser = watch('isExternalUser');

    return (
        <Tab tabId="1" toggle={toggle} className="pt-3">
            <ValidatedInput<UserItem>
                id="technical-user"
                name="isTechnicalUser"
                type="checkbox"
                label={t('BackOfficeIsTechnicalUser')}
            />
            <hr />
            <ValidatedInput<UserItem>
                name="userName"
                label={t('BackOfficeUsername')}
                rules={
                    disabled || !!currentItem
                        ? undefined
                        : {
                              required: requiredRule,
                              pattern: userNameRule,
                          }
                }
                disabled={disabled || !!currentItem}
            />
            <ValidatedInput<UserItem>
                name="email"
                type="email"
                label={t('BackOfficeEmail')}
                disabled={disabled}
            />
            <ValidatedInput<UserItem>
                name="phoneNumber"
                maskProps={{
                    mask: getMaskFromName('Default'),
                    changeMask: getMaskFromPhone,
                }}
                label={t('BackOfficePhoneNumber')}
                rules={{
                    required: requiredRule,
                    validate: (value) => checkPhoneLengthFromMask(value),
                }}
                disabled={disabled}
            />
            <ValidatedInput<UserItem>
                name="lastName"
                label={t('BackOfficeLastName')}
                rules={{
                    required: requiredRule,
                }}
                disabled={disabled}
            />
            <ValidatedInput<UserItem>
                name="firstName"
                label={t('BackOfficeFirstName')}
                rules={{
                    required: requiredRule,
                }}
                disabled={disabled}
            />
            <ValidatedInput<UserItem>
                name="middleName"
                label={t('BackOfficeMiddleName')}
                disabled={disabled}
            />
            <ValidatedInput<UserItem>
                id="is-external-user"
                type="checkbox"
                name="isExternalUser"
                label={`${t('BackOfficeExternalUser')} (${t('BackOfficeExternalUserHint')})`}
                disabled={disabled}
            />
            <ValidatedDropDownList<UserItem>
                name="crmRole"
                label={t('BackOfficeRolesForCRM')}
                items={crmRoles}
                isLoading={loadingCrmRoles}
                disabled={disabled}
                isClearable={false}
                isSearchable={false}
                rules={{
                    validate: (val) => {
                        if (!isExternalUser && !val) {
                            return 'BackOfficeFieldRequired';
                        }
                    },
                }}
            />
            {!currentItem && (
                <ValidatedInput
                    id="info-password-field"
                    type="password"
                    name="password"
                    label={t('BackOfficePassword')}
                    rules={{
                        required: requiredRule,
                        pattern: {
                            value: /^(?=.*\d)(?=.*[a-z])(?=.*[A-Z]).{6,}$/,
                            message: t('BackOfficePasswordRulesError'),
                        },
                    }}
                    disabled={disabled}
                    autoComplete="new-password"
                />
            )}
        </Tab>
    );
};

export default MainTab;
